import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { LocateFixed, Zap, Maximize2, Minimize2, Info, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { powerColor } from "../lib/power";
import { findNetwork } from "../lib/chargerNetworks";
import { isTripToday } from "../lib/geo";
import { CARTO_DARK } from "../lib/mapTiles";

const poiIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:50%;background:#E07A5F;display:flex;align-items:center;justify-content:center;color:white;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.4)"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const userIcon = L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#3B82F6;border:3px solid white;box-shadow:0 0 0 6px rgba(59,130,246,0.35)"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const stopIcon = (kw) => L.divIcon({
  className: "",
  html: `<div style="width:24px;height:24px;border-radius:50%;background:${powerColor(kw)};display:flex;align-items:center;justify-content:center;color:#fff;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.4)"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const stayIcon = L.divIcon({
  className: "",
  html: `<div style="width:26px;height:26px;border-radius:50%;background:#3A6EA5;display:flex;align-items:center;justify-content:center;color:#fff;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.4)"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/></svg></div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

const CHARGER_STATUS = {
  AVAILABLE: { color: "#22C55E", label: "Beschikbaar" },
  CHARGING: { color: "#F59E0B", label: "Bezet — aan het laden" },
  INOPERATIVE: { color: "#DC2626", label: "Buiten dienst" },
  UNKNOWN: { color: "#9CA3AF", label: "Status onbekend" },
};

const fastIcon = (c) => L.divIcon({
  className: "",
  html: `<div style="position:relative;width:22px;height:22px;border-radius:50%;background:${powerColor(c.max_power_kw)};display:flex;align-items:center;justify-content:center;color:#fff;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.4)"><svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>${CHARGER_STATUS[c.status] ? `<span style="position:absolute;top:-3px;right:-3px;width:9px;height:9px;border-radius:50%;background:${(CHARGER_STATUS[c.status] || CHARGER_STATUS.UNKNOWN).color};border:1.5px solid white"></span>` : ""}</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const distKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const nearestIdx = (geometry, pt, from = 0) => {
  let best = from;
  let bestD = Infinity;
  for (let i = from; i < geometry.length; i++) {
    const d = (geometry[i][0] - pt.lat) ** 2 + (geometry[i][1] - pt.lon) ** 2;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
};

const FollowUser = ({ pos, follow, nextStop, dayNumber }) => {
  const map = useMap();
  const fittedDay = useRef(null);
  useEffect(() => {
    if (!pos || !follow) return;
    if (nextStop?.lat && fittedDay.current !== dayNumber) {
      fittedDay.current = dayNumber;
      map.fitBounds(L.latLngBounds([[pos.lat, pos.lon], [nextStop.lat, nextStop.lon]]), { padding: [60, 60], maxZoom: 14 });
      return;
    }
    map.setView([pos.lat, pos.lon], fittedDay.current != null ? map.getZoom() : Math.max(map.getZoom(), 13), { animate: true });
  }, [pos, follow, nextStop, dayNumber, map]);
  return null;
};

const InvalidateOnResize = ({ trigger }) => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 180);
    return () => clearTimeout(t);
  }, [trigger, map]);
  useEffect(() => {
    const onR = () => setTimeout(() => map.invalidateSize(), 250);
    window.addEventListener("resize", onR);
    window.addEventListener("orientationchange", onR);
    return () => {
      window.removeEventListener("resize", onR);
      window.removeEventListener("orientationchange", onR);
    };
  }, [map]);
  return null;
};

const FitRoute = ({ geometry, center, active }) => {
  const map = useMap();
  useEffect(() => {
    if (!active) return;
    if (geometry?.length > 1) map.fitBounds(L.latLngBounds(geometry), { padding: [30, 30] });
    else if (center?.lat) map.setView([center.lat, center.lon], 12);
  }, [geometry, center, active, map]);
  return null;
};

export const LiveMap = ({ trip, day, onPosition, onRefresh }) => {
  const [mode, setMode] = useState(() => (isTripToday(trip) ? "live" : "planned"));
  const [pos, setPos] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [trail, setTrail] = useState(() => (trip.live_track || []).map((p) => ({ lat: p.lat, lon: p.lon, ts: p.ts })));
  const [lastFix, setLastFix] = useState(null);
  const [wakeOn, setWakeOn] = useState(false);
  const [, setTick] = useState(0);
  const wakeRef = useRef(null);
  const [liveRoute, setLiveRoute] = useState(null);
  const [follow, setFollow] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    const onPop = () => setFullscreen(false);
    window.history.pushState({ vrMapFs: true }, "");
    window.addEventListener("keydown", onKey);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("popstate", onPop);
      if (window.history.state?.vrMapFs) window.history.back();
    };
  }, [fullscreen]);
  const [pois, setPois] = useState([]);
  const [showChargers, setShowChargers] = useState(false);
  const [chargers, setChargers] = useState(null);
  const [chargersLoading, setChargersLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const bufferRef = useRef([]);
  const lastRerouteRef = useRef(null);
  const lastPoiRef = useRef(null);
  const shownPoisRef = useRef(new Set());

  const geometry = trip.route?.geometry || [];

  const dayTrail = useMemo(() => {
    if (!day?.date) return trail;
    return trail.filter((p) => !p.ts || String(p.ts).slice(0, 10) === day.date);
  }, [trail, day?.date]);

  const dayGeometry = useMemo(() => {
    if (geometry.length < 2 || !day || day.type === "stay" || !day.start || !day.end) return null;
    const driveDays = (trip.days || []).filter((d) => d.type !== "stay" && (d.distance_km || 0) > 0);
    let cursor = 0;
    for (let i = 0; i < driveDays.length; i++) {
      const d = driveDays[i];
      let endIdx = i === driveDays.length - 1 ? geometry.length - 1 : nearestIdx(geometry, d.end, cursor);
      if (endIdx <= cursor) endIdx = Math.min(cursor + 1, geometry.length - 1);
      if (d.day_number === day.day_number) {
        const seg = geometry.slice(cursor, endIdx + 1);
        return seg.length > 1 ? seg : null;
      }
      cursor = endIdx;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometry, trip.days, day?.day_number]);

  const drivenDay = useMemo(() => {
    if (!dayGeometry || dayGeometry.length < 2) return null;
    if (day?.arrived_at) return dayGeometry;
    const ref = pos || (dayTrail.length ? dayTrail[dayTrail.length - 1] : null);
    if (!ref) return null;
    const n = nearestIdx(dayGeometry, ref);
    if (distKm({ lat: dayGeometry[n][0], lon: dayGeometry[n][1] }, ref) > 15) return null;
    return n >= 1 ? dayGeometry.slice(0, n + 1) : null;
  }, [dayGeometry, pos, dayTrail, day?.arrived_at]);

  useEffect(() => {
    if (mode !== "live") return;
    if (!navigator.geolocation) {
      setGpsError("GPS niet beschikbaar op dit toestel");
      return;
    }
    const onFix = (p) => {
      const np = { lat: p.coords.latitude, lon: p.coords.longitude };
      setGpsError(null);
      setLastFix(Date.now());
      setPos(np);
      onPosition?.(np);
      setTrail((t) => {
        const last = t[t.length - 1];
        if (!last || distKm(last, np) > 0.02) {
          const ts = new Date().toISOString();
          bufferRef.current.push({ ...np, ts });
          return [...t, { ...np, ts }];
        }
        return t;
      });
    };
    navigator.geolocation.getCurrentPosition(onFix, () => {}, { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 });
    const id = navigator.geolocation.watchPosition(
      onFix,
      (e) => setGpsError(e?.code === 1 ? "Geen GPS-toegang — sta locatie toe in je browser" : "GPS-signaal zoeken mislukt — controleer je locatie-instellingen"),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [mode]);

  useEffect(() => {
    if (mode !== "live") return;
    const t = setInterval(() => setTick((x) => x + 1), 10000);
    return () => clearInterval(t);
  }, [mode]);

  useEffect(() => {
    if (mode !== "live" || !("wakeLock" in navigator)) return;
    let released = false;
    const acquire = async () => {
      try {
        wakeRef.current = await navigator.wakeLock.request("screen");
        setWakeOn(true);
        wakeRef.current.addEventListener("release", () => setWakeOn(false));
      } catch {
        setWakeOn(false);
      }
    };
    acquire();
    const onVis = () => { if (document.visibilityState === "visible" && !released) acquire(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVis);
      wakeRef.current?.release?.().catch(() => {});
      setWakeOn(false);
    };
  }, [mode]);

  useEffect(() => {
    if (mode !== "live") return;
    const t = setInterval(() => {
      const pts = bufferRef.current.splice(0);
      if (pts.length) api.post(`/trips/${trip.id}/track`, { points: pts }).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [mode, trip.id]);

  useEffect(() => {
    if (mode !== "live" || !pos || !day || day.type === "stay") return;
    const last = lastRerouteRef.current;
    if (last && distKm(last, pos) < 0.5) return;
    lastRerouteRef.current = pos;
    api.post(`/trips/${trip.id}/reroute`, { lat: pos.lat, lon: pos.lon, day_number: day.day_number })
      .then(({ data }) => setLiveRoute(data))
      .catch(() => {});
  }, [pos, mode, trip.id, day]);

  useEffect(() => {
    if (mode !== "live" || !pos) return;
    const last = lastPoiRef.current;
    if (last && distKm(last, pos) < 2) return;
    lastPoiRef.current = pos;
    api.get("/poi/near", { params: { lat: pos.lat, lon: pos.lon, radius: 5000 } })
      .then(({ data }) => {
        setPois(data);
        data.filter((p) => !shownPoisRef.current.has(p.id)).slice(0, 2).forEach((p) => {
          shownPoisRef.current.add(p.id);
          toast.custom((t) => (
            <div data-testid="poi-toast" className="w-[356px] max-w-[90vw] bg-[#EAF2FB] rounded-2xl shadow-xl p-4 flex gap-3">
              <a href={p.url} target="_blank" rel="noreferrer" data-testid="poi-toast-info"
                title="Meer info op Wikipedia"
                className="w-7 h-7 rounded-full bg-[#3A6EA5] text-white flex items-center justify-center shrink-0 mt-0.5 hover:bg-[#2B5580] active:scale-95 transition-[background-color,transform] duration-200">
                <Info className="w-4 h-4" />
              </a>
              <div className="flex-1 min-w-0">
                <a href={p.url} target="_blank" rel="noreferrer" className="font-heading font-bold text-[#1B3A5B] text-[15px] hover:underline block">
                  Je passeert: {p.title} ({p.distance_km} km)
                </a>
                {p.description && <p className="text-[#3A6EA5] text-sm leading-snug mt-1 line-clamp-3">{p.description}</p>}
                <a href={p.url} target="_blank" rel="noreferrer" className="text-[#3A6EA5] text-xs font-semibold mt-1.5 inline-block hover:underline">Lees meer →</a>
              </div>
              <button data-testid="poi-toast-close" onClick={() => toast.dismiss(t)} title="Sluiten"
                className="w-7 h-7 rounded-full hover:bg-[#3A6EA5]/15 text-[#3A6EA5] flex items-center justify-center shrink-0 transition-[background-color] duration-200">
                <X className="w-4 h-4" />
              </button>
            </div>
          ), { duration: 15000 });
        });
      })
      .catch(() => {});
  }, [pos, mode]);

  const center = dayGeometry ? dayGeometry[Math.floor(dayGeometry.length / 2)] : geometry.length ? geometry[Math.floor(geometry.length / 2)] : [50.85, 4.35];
  const stops = (day?.charging_stops || []).filter((cs) => cs.lat);

  const fetchChargers = async () => {
    setChargersLoading(true);
    try {
      const { data } = await api.get("/chargers/route", { params: { trip_id: trip.id, min_power: 100, ...(day?.type !== "stay" && day?.day_number ? { day_number: day.day_number } : {}) } });
      setChargers(data);
    } catch {
      toast.error("Snelladers ophalen mislukt — probeer opnieuw");
      setShowChargers(false);
    } finally {
      setChargersLoading(false);
    }
  };

  const toggleChargers = () => {
    if (showChargers) { setShowChargers(false); return; }
    setShowChargers(true);
    fetchChargers();
  };

  useEffect(() => {
    if (showChargers) fetchChargers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day?.day_number]);

  const addAsStop = async (c) => {
    setAddingId(c.id);
    try {
      const { data } = await api.post(`/trips/${trip.id}/days/${day.day_number}/charging`, {
        name: c.name, town: c.address?.town || "", lat: c.coordinates.lat, lon: c.coordinates.lon,
        operator: c.operator || null, max_power_kw: c.max_power_kw || null,
        connectors: (c.evses?.[0]?.connectors || []).slice(0, 4).map((con) => ({ type: con.standard, power_kw: con.power_kw })),
      });
      toast.success(`${data.name} toegevoegd als laadstop op dag ${day.day_number}`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAddingId(null);
    }
  };

  const fixAgeS = lastFix ? Math.round((Date.now() - lastFix) / 1000) : null;
  const fixFresh = fixAgeS != null && fixAgeS < 60;
  const fixText = fixAgeS == null ? "GPS zoeken…" : fixAgeS < 15 ? "GPS live" : fixAgeS < 60 ? `GPS ${fixAgeS}s geleden` : `GPS ${Math.round(fixAgeS / 60)}m geleden`;

  return (
    <div className={fullscreen ? "vr-map-fs fixed inset-0 z-[80] bg-[#0F172A]" : "relative h-[42vh] rounded-2xl overflow-hidden mb-6"} data-testid="live-map">
      <MapContainer center={center} zoom={8} scrollWheelZoom className="w-full h-full">
        <TileLayer url={CARTO_DARK} attribution='&copy; OSM &copy; CARTO' />
        <InvalidateOnResize trigger={fullscreen} />
        <FitRoute geometry={dayGeometry} center={day?.end || day?.start} active={mode === "planned" || !pos} />
        <FollowUser pos={pos} follow={follow && mode === "live"} nextStop={stops[0]} dayNumber={day?.day_number} />
        {dayGeometry && (
          <Polyline positions={dayGeometry} pathOptions={{ color: "#52B788", weight: 5, opacity: mode === "live" ? 0.3 : drivenDay ? 0.45 : 0.9 }} />
        )}
        {drivenDay && drivenDay.length > 1 && (
          <Polyline positions={drivenDay} pathOptions={{ color: "#16A34A", weight: 6, opacity: 0.95, lineCap: "round" }} />
        )}
        {mode === "live" && liveRoute?.geometry?.length > 1 && (
          <Polyline positions={liveRoute.geometry} pathOptions={{ color: "#52B788", weight: 5, opacity: 0.95 }} />
        )}
        {dayTrail.length > 1 && (
          <Polyline positions={dayTrail.map((p) => [p.lat, p.lon])} pathOptions={{ color: "#F59E0B", weight: 4, opacity: mode === "live" ? 0.9 : 0.6 }} />
        )}
        {stops.map((cs, i) => (
          <Marker key={i} position={[cs.lat, cs.lon]} icon={stopIcon(cs.max_power_kw)}>
            {(() => { const nw = findNetwork(cs.operator); return (
              <Tooltip direction="top" offset={[0, -12]} opacity={1}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {nw?.logo && <img src={nw.logo} alt={nw.label} style={{ height: 20, maxWidth: 80, objectFit: "contain" }} />}
                  <span>
                    <strong style={{ display: "block", fontSize: 12, lineHeight: 1.3 }}>{nw ? nw.label : cs.operator || cs.name}</strong>
                    <span style={{ fontSize: 11, color: "#6B7280" }}>{cs.name}{cs.max_power_kw ? ` · ${cs.max_power_kw} kW` : ""}</span>
                  </span>
                </span>
              </Tooltip>
            ); })()}
            <Popup><strong>{cs.name}</strong><br />{cs.max_power_kw ? `${cs.max_power_kw} kW · ` : ""}±{cs.charge_minutes} min</Popup>
          </Marker>
        ))}
        {day?.end?.lat != null && (day?.stay || day?.type === "stay") && (
          <Marker position={[day.end.lat, day.end.lon]} icon={stayIcon} zIndexOffset={1500} data-testid="live-stay-marker">
            <Popup>
              <strong>Overnachting{day.stay ? `: ${day.stay.name}` : ""}</strong>
              {day.stay?.type === "eigen boeking" && <><br /><em data-testid="live-map-stay-lock"><Lock style={{ width: 11, height: 11, verticalAlign: -1, marginRight: 3, color: "#059669", display: "inline" }} />eigen boeking — bevestigd</em></>}
              <br />{(day.end.name || "").split(",").slice(0, 2).join(",")}
              <br /><a href={`https://www.google.com/maps/dir/?api=1&destination=${day.end.lat},${day.end.lon}`} target="_blank" rel="noreferrer">Navigeer met Google Maps</a>
            </Popup>
          </Marker>
        )}
        {showChargers && (chargers || []).map((c) => {
          const st = CHARGER_STATUS[c.status] || CHARGER_STATUS.UNKNOWN;
          const freePoints = (c.evses || []).filter((e) => e.status === "AVAILABLE").length;
          return (
            <Marker key={c.id} position={[c.coordinates.lat, c.coordinates.lon]} icon={fastIcon(c)}>
              <Popup>
                <strong>{c.name}</strong><br />
                <span data-testid={`charger-status-${c.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: st.color, display: "inline-block" }} />
                  <strong style={{ color: st.color }}>{st.label}</strong>
                </span><br />
                {c.operator && <>{c.operator}<br /></>}
                {c.max_power_kw ? `${c.max_power_kw} kW` : "vermogen onbekend"}
                {c.num_points ? ` · ${c.evses?.length ? `${freePoints} van ${c.num_points}` : c.num_points} laadpunt${c.num_points === 1 ? "" : "en"}${c.evses?.length ? " beschikbaar" : ""}` : ""}<br />
                {day?.type !== "stay" && (
                  <button data-testid={`add-charger-stop-${c.id}`} onClick={() => addAsStop(c)} disabled={addingId === c.id}
                    style={{ marginTop: 6, background: "#52B788", color: "#0F172A", border: "none", borderRadius: 999, padding: "6px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer", opacity: addingId === c.id ? 0.6 : 1 }}>
                    {addingId === c.id ? "Toevoegen..." : `+ Voeg toe als laadstop dag ${day.day_number}`}
                  </button>
                )}
              </Popup>
            </Marker>
          );
        })}
        {mode === "live" && pois.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lon]} icon={poiIcon}>
            <Popup>
              <strong>{p.title}</strong><br />
              {p.description?.slice(0, 180)}<br />
              <a href={p.url} target="_blank" rel="noreferrer">Lees meer</a>
            </Popup>
          </Marker>
        ))}
        {mode === "live" && pos && <Marker position={[pos.lat, pos.lon]} icon={userIcon} />}
      </MapContainer>

      <div className="absolute top-3 left-3 z-[1000] flex bg-[#0F172A]/85 backdrop-blur rounded-full p-1">
        <button data-testid="route-toggle-planned" onClick={() => setMode("planned")}
          className={`px-4 py-1.5 rounded-full text-xs font-bold transition-[background-color,color] duration-200 ${mode === "planned" ? "bg-[#52B788] text-[#0F172A]" : "text-white/70"}`}>
          Gepland
        </button>
        <button data-testid="route-toggle-live" onClick={() => setMode("live")}
          className={`px-4 py-1.5 rounded-full text-xs font-bold transition-[background-color,color] duration-200 ${mode === "live" ? "bg-[#52B788] text-[#0F172A]" : "text-white/70"}`}>
          Live
        </button>
      </div>

      <button data-testid="live-chargers-toggle" onClick={toggleChargers} title="Snelladers (100 kW+) langs de route"
        className={`vr-ctl-tr absolute top-14 right-3 z-[1000] h-9 rounded-full px-3.5 flex items-center gap-1.5 text-xs font-bold backdrop-blur transition-[background-color,color] duration-200 ${showChargers ? "bg-[#FBBF24] text-[#0F172A]" : "bg-[#0F172A]/85 text-white"}`}>
        <Zap className={`w-4 h-4 ${chargersLoading ? "animate-pulse" : ""}`} />
        {chargersLoading ? "Zoeken..." : showChargers ? `${(chargers || []).length} snelladers` : "Snelladers"}
      </button>

      <button data-testid="live-map-fullscreen-toggle" onClick={() => setFullscreen((f) => !f)} title={fullscreen ? "Verlaat volledig scherm" : "Kaart op volledig scherm"}
        className={`vr-ctl-tr2 absolute top-[6.25rem] right-3 z-[1000] w-9 h-9 rounded-full flex items-center justify-center backdrop-blur transition-[background-color] duration-200 ${fullscreen ? "bg-[#52B788] text-[#0F172A]" : "bg-[#0F172A]/85 text-white"}`}>
        {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
      </button>

      {fullscreen && (
        <button data-testid="live-map-exit-fullscreen" onClick={() => setFullscreen(false)}
          className="vr-ctl-bc absolute bottom-5 left-1/2 -translate-x-1/2 z-[1001] h-12 rounded-full px-6 flex items-center gap-2 text-sm font-bold bg-[#52B788] text-[#0F172A] shadow-[0_8px_30px_rgba(82,183,136,0.45)] active:scale-95 transition-[transform] duration-200">
          <Minimize2 className="w-4 h-4" /> Kaart verkleinen
        </button>
      )}

      {mode === "live" && (
        <button data-testid="live-follow-button" onClick={() => setFollow((f) => !f)}
          className={`vr-ctl-br absolute bottom-3 right-3 z-[1000] w-11 h-11 rounded-full flex items-center justify-center backdrop-blur transition-[background-color] duration-200 ${follow ? "bg-[#52B788] text-[#0F172A]" : "bg-[#0F172A]/85 text-white"}`}>
          <LocateFixed className="w-5 h-5" />
        </button>
      )}

      {mode === "live" && (gpsError ? (
        <div data-testid="live-gps-status" className="vr-ctl-tr0 absolute top-3 right-3 z-[1000] bg-amber-500/90 text-[#0F172A] text-xs font-semibold px-3 py-1.5 rounded-full max-w-[55%]">
          {gpsError}
        </div>
      ) : (
        <div data-testid="live-gps-status" className={`vr-ctl-tr0 absolute top-3 right-3 z-[1000] backdrop-blur text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 max-w-[60%] ${fixFresh ? "bg-[#0F172A]/85 text-[#52B788]" : "bg-amber-500/90 text-[#0F172A]"}`}>
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${fixFresh ? "bg-[#52B788] animate-pulse" : "bg-[#0F172A]"}`} />
          <span className="truncate">{fixText}{wakeOn ? " · scherm blijft aan" : ""}</span>
        </div>
      ))}

      {mode === "live" && liveRoute && (
        <div data-testid="live-route-stats" className="vr-ctl-bl absolute bottom-3 left-3 z-[1000] bg-[#0F172A]/85 backdrop-blur text-white text-xs font-mono px-4 py-2 rounded-full">
          {liveRoute.distance_km} km · ±{liveRoute.duration_hours}u → {liveRoute.destination}
        </div>
      )}
    </div>
  );
};

